import {
  BehaviorSubject,
  filter,
  type InteropObservable,
  type Observable,
  type Subscribable,
} from 'rxjs';
import type { RouteNode } from './routeNode';
import {
  isNotFoundLocation,
  isUnmatchedLocation,
  type ArrivedLocation,
  type LocationIn,
  type MatchedLocationIn,
  type NotFoundLocation,
  type UnmatchedLocation,
} from './location';
import { matchLocation, matchPath } from './declarativeRouterLogic';
import type { Params } from './paramRegistry';
import { isTruthy } from '@ardoq/common-helpers';

type RouterLocation<RouteTree extends RouteNode> =
  | MatchedLocationIn<RouteTree>
  | NotFoundLocation
  | UnmatchedLocation;

/**
 * The core observable (stream) in the declarative router.
 *
 * Will be used by both application code and router code.
 * The two main code paths are:
 *
 * 1. During browser navigation (hard reload or back/forward navigation):
 *   - The browser URL changes
 *   - The router code calls `arrive(path)` with the new URL path
 *   - The location$ (instance of this class) emits the matched location based on the route tree
 *   - The `connectRouter` component updates the UI accordingly
 *   - The router code updates the browser URL in case the matched location doesn't fully match the given URL path
 *
 * 2. During programmatic navigation (e.g. triggered by application code):
 *   - Application code calls `depart(location)` with a location to navigate to
 *   - The router code matches the location against the route tree
 *   - The location$ (instance of this class) emits the matched location
 *   - The `connectRouter` component updates the UI accordingly
 *   - The router code updates the browser URL to match the new location
 */
export class DeclarativeRouter<
  RouteTree extends RouteNode,
> implements InteropObservable<RouterLocation<RouteTree>> {
  public _routeTree: RouteTree;
  get routeTree() {
    return this._routeTree;
  }

  private subject: BehaviorSubject<RouterLocation<RouteTree> | null>;
  private observable: Observable<RouterLocation<RouteTree>>;
  public pipe: Observable<RouterLocation<RouteTree>>['pipe'];

  constructor(routeTree: RouteTree) {
    this._routeTree = routeTree;
    this.subject = new BehaviorSubject<RouterLocation<RouteTree> | null>(null);
    this.observable = this.subject.pipe(filter(isTruthy));

    this.pipe = this.observable.pipe.bind(this.observable);
  }

  [Symbol.observable](): Subscribable<RouterLocation<RouteTree>> {
    return this.observable;
  }

  hmr(routeTree: RouteTree) {
    this._routeTree = routeTree;
    const current = this.subject.value;
    if (!current) {
      return;
    }

    const rematched =
      isNotFoundLocation(current) || isUnmatchedLocation(current)
        ? current
        : matchLocation<RouteTree>(current, routeTree, current.action);
    this.subject.next({ ...rematched, isHmrRender: true });
  }

  /**
   * "Arrives" at a location by matching the path against the route tree.
   *
   * Mainly intended for use in the router implementation, to update the `location$` with the latest path from the
   * browser, but can also be useful in tests.
   *
   * The matched location is emitted, and also returned directly.
   *
   * @param path Path the router arrives at.
   * @param searchParams Optional URL search parameters.
   * @param hiddenParams Optional hidden parameters from browser history state.
   * @param hash Optional URL hash fragment, including the leading `#`.
   * @param action History action to use (defaults to 'replace').
   * @returns The matched location of the path in the route tree, a not found
   * location if the path only partially matched, or false if nothing matched.
   */
  arrive(
    path: string,
    searchParams?: URLSearchParams,
    hiddenParams?: Partial<Params>,
    hash?: string,
    action: 'push' | 'replace' = 'replace'
  ): false | ArrivedLocation<RouteTree> {
    const parsed = matchPath(
      path,
      searchParams,
      this._routeTree,
      hiddenParams,
      hash,
      action
    );
    if (isUnmatchedLocation(parsed)) {
      return false;
    }

    this.subject.next(parsed);
    return parsed;
  }

  /**
   * Departs to a location by matching the location against the route tree.
   *
   * The matched location is emitted, which should be used to update the UI and the browser's URL.
   *
   * @param location Location to depart to.
   * @param action History action to use (defaults to 'push').
   */
  depart(
    location: LocationIn<RouteTree>,
    action: 'push' | 'replace' = 'push'
  ): void {
    const matched = matchLocation<RouteTree>(location, this._routeTree, action);
    this.subject.next(matched);
  }
}
